
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { NAV_ITEMS, Icons, IMAGES } from '../constants';
import { CONTENT } from '../content';

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [logoError, setLogoError] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScrollEvent = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScrollEvent);
    return () => window.removeEventListener('scroll', handleScrollEvent);
  }, []);

  const handleLinkClick = (e: React.MouseEvent, path: string) => {
    if (path.startsWith('#')) {
      e.preventDefault();
      if (location.pathname !== '/') {
        navigate('/' + path);
      } else {
        const elementId = path.replace('#', '');
        const element = document.getElementById(elementId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    } else if (path === 'faq') {
      e.preventDefault();
      window.dispatchEvent(new CustomEvent('openFAQModal'));
    }
    
    if (isOpen) setIsOpen(false);
    setActiveDropdown(null);
  };

  const isDark = scrolled || location.pathname !== '/';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isDark ? 'bg-white/95 backdrop-blur-sm py-4 shadow-sm' : 'bg-transparent py-6'}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex justify-between items-center h-16">
          <div className="flex-shrink-0">
            <Link to="/" onClick={(e) => handleLinkClick(e, '#home')} className="flex items-center group">
              {IMAGES.logo && !logoError ? (
                <img 
                  src={IMAGES.logo} 
                  alt="Liza Moerman Coaching Logo" 
                  className={`h-10 lg:h-12 w-auto object-contain transition-all duration-300 ${!isDark ? 'brightness-[10] contrast-[10]' : ''}`}
                  width="160"
                  height="48"
                  loading="eager"
                  onError={() => {
                    console.log("Logo failed to load, falling back to text.");
                    setLogoError(true);
                  }}
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="flex flex-col leading-none">
                  <span className={`text-xl lg:text-2xl font-light tracking-[0.25em] uppercase group-hover:tracking-[0.3em] transition-all duration-500 ${isDark ? 'text-primary' : 'text-white'}`}>LIZA MOERMAN</span>
                  <span className={`text-[9px] tracking-[0.5em] uppercase mt-2 font-medium ${isDark ? 'text-accent' : 'text-white/80'}`}>COACHING</span>
                </div>
              )}
            </Link>
          </div>
          
          <div className="hidden lg:flex items-center lg:space-x-5 xl:space-x-8">
            <div className="flex items-center lg:space-x-6 xl:space-x-9">
              {NAV_ITEMS.map((item) => (
                <div 
                  key={item.label} 
                  className="relative group"
                  onMouseEnter={() => item.children && setActiveDropdown(item.label)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <Link
                    to={item.path.startsWith('#') ? (location.pathname === '/' ? item.path : '/' + item.path) : '/' + item.path}
                    onClick={(e) => handleLinkClick(e, item.path)}
                    className={`text-[10px] uppercase tracking-[0.2em] font-semibold transition-all duration-300 flex items-center gap-1.5 py-2 whitespace-nowrap after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-0 after:h-[1px] after:bg-primary hover:after:w-full after:transition-all after:duration-300 ${isDark ? 'text-gray-500 hover:text-primary' : 'text-white/90 hover:text-white'}`}
                  >
                    {item.label}
                    {item.children && <Icons.ChevronDown />}
                  </Link>

                  {item.children && (
                    <div className={`absolute left-0 top-full pt-4 transition-all duration-300 ${activeDropdown === item.label ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-2 pointer-events-none'}`}>
                      <div className="bg-white rounded-2xl shadow-xl border border-accent-light p-4 min-w-[220px] flex flex-col gap-1">
                        {item.children.map((child) => (
                          <Link
                            key={child.label}
                            to={child.path.startsWith('#') ? (location.pathname === '/' ? child.path : '/' + child.path) : '/' + child.path}
                            onClick={(e) => handleLinkClick(e, child.path)}
                            className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-500 hover:text-primary hover:bg-liza-bg p-3 rounded-xl transition-all"
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <Link
              to="/contact"
              className="bg-primary text-white px-7 py-3 text-[10px] uppercase tracking-[0.25em] font-bold hover:shadow-[0_10px_20px_-10px_rgba(172,107,107,0.5)] hover:-translate-y-0.5 transition-all duration-300 rounded-full whitespace-nowrap"
            >
              {CONTENT.navigation.ctaButton}
            </Link>
          </div>

          <div className="lg:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`${isDark ? 'text-primary' : 'text-white'} focus:outline-none p-2`}
            >
              <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`lg:hidden absolute top-full left-0 right-0 bg-white border-t border-accent-light transition-all duration-500 ease-in-out ${isOpen ? 'max-h-screen py-8 shadow-2xl opacity-100 overflow-y-auto' : 'max-h-0 opacity-0 overflow-hidden'}`}>
        <div className="px-8 space-y-6">
          {NAV_ITEMS.map((item) => (
            <div key={item.label} className="space-y-4">
              <div className="flex items-center justify-between w-full">
                <Link
                  to={item.path.startsWith('#') ? (location.pathname === '/' ? item.path : '/' + item.path) : '/' + item.path}
                  onClick={(e) => !item.children && handleLinkClick(e, item.path)}
                  className="text-[11px] uppercase tracking-[0.3em] font-medium text-gray-600"
                >
                  {item.label}
                </Link>
                {item.children && (
                  <button onClick={() => setActiveDropdown(activeDropdown === item.label ? null : item.label)} className="p-2 text-accent">
                    <Icons.ChevronDown />
                  </button>
                )}
              </div>
              
              {item.children && activeDropdown === item.label && (
                <div className="pl-4 flex flex-col gap-4 animate-in slide-in-from-top-2">
                  {item.children.map((child) => (
                    <Link
                      key={child.label}
                      to={child.path.startsWith('#') ? (location.pathname === '/' ? child.path : '/' + child.path) : '/' + child.path}
                      onClick={(e) => handleLinkClick(e, child.path)}
                      className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-400 hover:text-primary"
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          <Link
            to="/contact"
            onClick={() => setIsOpen(false)}
            className="block w-full text-center bg-primary text-white py-4 text-[11px] uppercase tracking-[0.3em] font-bold rounded-full"
          >
            {CONTENT.navigation.ctaButton}
          </Link>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
